import { DAY_LENGTH_MINUTES, NIGHT_HOURS, isDayOver, nightIncome, type JobOutcome, type StatName } from '@bratva/rules';
import {
  COMPANION_PATIENCE_DAYS,
  afterRaid,
  levelFromXp,
  nightHeatRelief,
  stageOf,
  statPointsFor,
} from '@bratva/rules';
import type {
  BrigadeSlot,
  Business,
  Companion,
  GearSlotKey,
  HabarItem,
  HeldTerritory,
  LogEntry,
  QuestState,
  SaveDoc,
} from './save.js';

/**
 * Единственный способ изменить состояние. Ни сцена, ни компоненты
 * не правят документ напрямую — иначе через месяц никто не найдёт,
 * кто списал деньги.
 *
 * Броски и формулы живут в правилах: сюда приезжает готовый результат,
 * а reducer только раскладывает его по документу.
 */
export type Action =
  | { type: 'walk'; to: { x: number; y: number }; minutes: number }
  | { type: 'travel'; zone: string; to: { x: number; y: number }; minutes: number; label: string }
  | { type: 'spendMinutes'; minutes: number; text?: string }
  | { type: 'money'; amount: number; reason: string }
  | {
      type: 'job';
      title: string;
      minutes: number;
      outcome: JobOutcome;
      /** Что унёс: хабар кладётся в руки, деньги — в карман. */
      loot?: HabarItem;
      /** Объект, который после дела считается тронутым. */
      touch?: string;
      /** Опыт за дело: начисляется только за успех. */
      xp?: number;
    }
  | { type: 'sell'; minutes: number; amount: number; count: number; heat: number }
  | { type: 'buy'; minutes: number; price: number; business: Business; stress: number }
  | { type: 'train'; minutes: number; price: number; stat: StatName; stress: number }
  | {
      type: 'rest';
      title: string;
      minutes: number;
      stressRelief: number;
      healthRelief: number;
      heatRelief: number;
      price: number;
      touch?: string;
    }
  | { type: 'court'; title: string; minutes: number; price: number; stress: number; companion?: Companion; touch?: string }
  | { type: 'date'; minutes: number; companion: Companion; touch?: string }
  | { type: 'buyItem'; def: string; title: string; price: number; minutes: number }
  | { type: 'buyVehicle'; def: string; title: string; price: number; minutes: number }
  | { type: 'setVehicle'; def: string | null; title: string }
  | { type: 'equip'; slot: GearSlotKey; def: string | null; title: string }
  | { type: 'spendStatPoint'; stat: 'sila' | 'smekalochka' | 'obayanie' | 'chotkost' }
  | { type: 'chooseClass'; classId: string; title: string }
  | { type: 'chooseSubclass'; subclassId: string; title: string }
  | { type: 'talk'; title: string; npc: string; minutes: number; reputation: number; touch?: string }
  | { type: 'gift'; title: string; npc: string; minutes: number; price: number; reputation: number; touch?: string }
  | { type: 'bribe'; title: string; minutes: number; price: number; heatRelief: number }
  | {
      type: 'fight';
      title: string;
      minutes: number;
      won: boolean;
      damage: number;
      payout: number;
      avtoritet: number;
      heat: number;
      stress: number;
      log: string[];
      touch?: string;
    }
  | {
      type: 'takeover';
      title: string;
      territoryId: string;
      territoryTitle: string;
      minutes: number;
      won: boolean;
      damage: number;
      avtoritet: number;
      heat: number;
      stress: number;
      log: string[];
    }
  | { type: 'hire'; title: string; minutes: number; price: number; slot: BrigadeSlot }
  | { type: 'loseTerritory'; territoryId: string; territoryTitle: string }
  | { type: 'settleCourt'; price: number; how: string }
  | { type: 'finishRun'; outcome: 'srok' | 'mogila'; text: string }
  | { type: 'hospital'; lostCash: number }
  | { type: 'questStep'; questId: string; title: string; step: number; text: string }
  | {
      type: 'questDone';
      questId: string;
      title: string;
      reward: { cash?: number; xp?: number; avtoritet?: number; item?: string; text: string };
    }
  | { type: 'raid'; lostCash: number; lostHabar: number; reason: string }
  | {
      type: 'sleep';
      /** Суммарная доля с территорий, в процентах: считает игра по контенту. */
      territoryBonus?: number;
    }
  | { type: 'log'; text: string };

export function reduce(save: SaveDoc, action: Action): SaveDoc {
  switch (action.type) {
    case 'walk': {
      const minute = clampMinute(save.minute + action.minutes);
      return { ...save, minute, player: { ...save.player, tile: action.to } };
    }

    case 'travel': {
      const minute = clampMinute(save.minute + action.minutes);
      return withLog(
        { ...save, minute, player: { ...save.player, zone: action.zone, tile: action.to } },
        { day: save.day, minute, text: `${action.label}: дорога ${action.minutes} мин.` },
      );
    }

    case 'spendMinutes': {
      const minute = clampMinute(save.minute + action.minutes);
      const next = { ...save, minute };
      return action.text ? withLog(next, { day: save.day, minute, text: action.text }) : next;
    }

    case 'money':
      return applyMoney(save, action.amount, action.reason);

    case 'job':
      return applyJob(save, action);

    case 'sell': {
      const minute = clampMinute(save.minute + action.minutes);
      const withoutHabar: SaveDoc = {
        ...save,
        minute,
        habar: [],
        counters: bumpCounter(save.counters, 'sold'),
        player: { ...save.player, heat: clampGauge(save.player.heat + action.heat) },
      };
      return applyMoney(withoutHabar, action.amount, `Сбыл в приёмке: ${action.count} шт.`);
    }

    case 'buy': {
      const minute = clampMinute(save.minute + action.minutes);
      const bought: SaveDoc = {
        ...save,
        minute,
        businesses: [...save.businesses, action.business],
        player: { ...save.player, stress: clampGauge(save.player.stress + action.stress) },
      };
      return applyMoney(bought, -action.price, `Выкупил: ${action.business.title}`);
    }

    case 'train': {
      const minute = clampMinute(save.minute + action.minutes);
      const trained: SaveDoc = {
        ...save,
        minute,
        player: {
          ...save.player,
          [action.stat]: save.player[action.stat] + 1,
          stress: clampGauge(save.player.stress + action.stress),
        },
      };
      return applyMoney(trained, -action.price, `Потренировался: ${statTitle(action.stat)} +1`);
    }

    case 'rest': {
      const minute = clampMinute(save.minute + action.minutes);
      const touched = action.touch ? [...save.world.touched, action.touch] : save.world.touched;
      const rested = withLog(
        {
          ...save,
          minute,
          world: { ...save.world, touched },
          player: {
            ...save.player,
            stress: clampGauge(save.player.stress - action.stressRelief),
            health: clampGauge(save.player.health + action.healthRelief),
            heat: clampGauge(save.player.heat - action.heatRelief),
          },
        },
        {
          day: save.day,
          minute,
          text: action.heatRelief > 0
            ? `${action.title}: палево −${action.heatRelief}.`
            : `${action.title}: стресс −${action.stressRelief}.`,
        },
      );
      return action.price > 0 ? applyMoney(rested, -action.price, action.title) : rested;
    }

    case 'raid': {
      // Облава забирает хабар целиком и часть налички, роняет здоровье
      // и съедает остаток дня: до утра пацан уже никуда не идёт.
      const beaten: SaveDoc = {
        ...save,
        minute: DAY_LENGTH_MINUTES,
        habar: [],
        justice: afterRaid(save.justice, save.day),
        player: {
          ...save.player,
          health: clampGauge(save.player.health - 10),
          heat: 30,
          stress: clampGauge(save.player.stress + 15),
        },
      };
      const text = `${action.reason}. Хабар изъяли (${action.lostHabar} шт.), день кончен.`;
      return applyMoney(withLog(beaten, { day: save.day, minute: DAY_LENGTH_MINUTES, text }), -action.lostCash, 'Облава');
    }

    case 'court': {
      const minute = clampMinute(save.minute + action.minutes);
      const touched = action.touch ? [...save.world.touched, action.touch] : save.world.touched;
      const met: SaveDoc = {
        ...save,
        minute,
        world: { ...save.world, touched },
        companions: action.companion ? [...save.companions, action.companion] : save.companions,
        player: { ...save.player, stress: clampGauge(save.player.stress + action.stress) },
      };
      const text = action.companion
        ? `${action.title}: познакомился — ${action.companion.title}.`
        : `${action.title}: не сложилось.`;
      return applyMoney(withLog(met, { day: save.day, minute, text }), -action.price, action.title);
    }

    case 'date': {
      const minute = clampMinute(save.minute + action.minutes);
      const touched = action.touch ? [...save.world.touched, action.touch] : save.world.touched;
      const walked: SaveDoc = {
        ...save,
        minute,
        world: { ...save.world, touched },
        counters: bumpCounter(save.counters, 'dates'),
        // Прогулка отмечается днём: по ней считается терпение спутницы.
        companions: save.companions.map((companion) =>
          companion.def === action.companion.def ? { ...companion, lastDateDay: save.day } : companion,
        ),
        player: { ...save.player, stress: clampGauge(save.player.stress - action.companion.stressRelief) },
      };
      return applyMoney(
        withLog(walked, {
          day: save.day,
          minute,
          text: `Погулял с ${action.companion.title}: стресс −${action.companion.stressRelief}.`,
        }),
        -action.companion.giftPrice,
        'Подарок',
      );
    }

    case 'buyItem': {
      const minute = clampMinute(save.minute + action.minutes);
      const bought: SaveDoc = { ...save, minute, owned: [...save.owned, action.def] };
      return applyMoney(bought, -action.price, `Купил: ${action.title}`);
    }

    case 'buyVehicle': {
      const minute = clampMinute(save.minute + action.minutes);
      const bought: SaveDoc = {
        ...save,
        minute,
        garage: [...save.garage, action.def],
        counters: bumpCounter(save.counters, 'vehicles'),
        // Первая тачка сразу становится рабочей: пешком после неё никто не ходит.
        activeVehicle: save.activeVehicle ?? action.def,
      };
      return applyMoney(bought, -action.price, `Купил тачку: ${action.title}`);
    }

    case 'setVehicle':
      return withLog(
        { ...save, activeVehicle: action.def },
        {
          day: save.day,
          minute: save.minute,
          text: action.def ? `Сел за руль: ${action.title}` : 'Дальше пешком.',
        },
      );

    case 'equip': {
      const equipment = { ...save.equipment };
      if (action.def) equipment[action.slot] = action.def;
      else delete equipment[action.slot];
      return withLog(
        { ...save, equipment },
        {
          day: save.day,
          minute: save.minute,
          text: action.def ? `Надел: ${action.title}` : `Снял: ${action.title}`,
        },
      );
    }

    case 'spendStatPoint': {
      if (save.player.statPoints <= 0) return save;
      return withLog(
        {
          ...save,
          player: {
            ...save.player,
            statPoints: save.player.statPoints - 1,
            [action.stat]: save.player[action.stat] + 1,
          },
        },
        { day: save.day, minute: save.minute, text: `${statTitle(action.stat)} +1` },
      );
    }

    case 'chooseClass':
      return withLog(
        { ...save, player: { ...save.player, classId: action.classId } },
        { day: save.day, minute: save.minute, text: `Теперь ты ${action.title}.` },
      );

    case 'chooseSubclass':
      return withLog(
        { ...save, player: { ...save.player, subclassId: action.subclassId } },
        { day: save.day, minute: save.minute, text: `Теперь ты ${action.title}.` },
      );

    case 'talk': {
      const minute = clampMinute(save.minute + action.minutes);
      const touched = action.touch ? [...save.world.touched, action.touch] : save.world.touched;
      return withLog(
        { ...save, minute, world: { ...save.world, touched }, npc: bumpNpc(save, action.npc, action.reputation) },
        { day: save.day, minute, text: `${action.title}: отношения теплее.` },
      );
    }

    case 'gift': {
      const minute = clampMinute(save.minute + action.minutes);
      const touched = action.touch ? [...save.world.touched, action.touch] : save.world.touched;
      const given: SaveDoc = {
        ...save,
        minute,
        world: { ...save.world, touched },
        npc: bumpNpc(save, action.npc, action.reputation),
      };
      return applyMoney(given, -action.price, action.title);
    }

    case 'bribe': {
      const minute = clampMinute(save.minute + action.minutes);
      const paid: SaveDoc = {
        ...save,
        minute,
        player: { ...save.player, heat: clampGauge(save.player.heat - action.heatRelief) },
      };
      return applyMoney(paid, -action.price, `${action.title}: палево −${action.heatRelief}`);
    }

    case 'fight': {
      const minute = clampMinute(save.minute + action.minutes);
      const touched = action.touch ? [...save.world.touched, action.touch] : save.world.touched;
      let next: SaveDoc = {
        ...save,
        minute,
        world: { ...save.world, touched },
        counters: bumpCounter(save.counters, 'fights'),
        player: {
          ...save.player,
          health: clampGauge(save.player.health - action.damage),
          heat: clampGauge(save.player.heat + action.heat),
          stress: clampGauge(save.player.stress + action.stress),
          avtoritet: action.won ? save.player.avtoritet + action.avtoritet : save.player.avtoritet,
        },
      };
      // Лог драки читается как отчёт: по строке на раунд.
      for (const line of action.log) {
        next = withLog(next, { day: save.day, minute, text: line });
      }
      if (action.won && action.payout > 0) next = applyMoney(next, action.payout, `${action.title}: забрал своё`);
      return next;
    }

    case 'takeover': {
      const minute = clampMinute(save.minute + action.minutes);
      let next: SaveDoc = {
        ...save,
        minute,
        counters: bumpCounter(save.counters, 'fights'),
        territories: action.won
          ? [...save.territories, { id: action.territoryId, heldSince: save.day }]
          : save.territories,
        player: {
          ...save.player,
          health: clampGauge(save.player.health - action.damage),
          heat: clampGauge(save.player.heat + action.heat),
          stress: clampGauge(save.player.stress + action.stress),
          avtoritet: action.won ? save.player.avtoritet + action.avtoritet : save.player.avtoritet,
        },
      };
      for (const line of action.log) next = withLog(next, { day: save.day, minute, text: line });
      return withLog(next, {
        day: save.day,
        minute,
        text: action.won ? `${action.territoryTitle} теперь твой.` : `${action.territoryTitle} отбить не вышло.`,
      });
    }

    case 'hire': {
      const minute = clampMinute(save.minute + action.minutes);
      const hired: SaveDoc = { ...save, minute, brigade: [...save.brigade, action.slot] };
      return applyMoney(hired, -action.price, `Взял в бригаду: ${action.slot.title}`);
    }

    case 'loseTerritory':
      return withLog(
        { ...save, territories: save.territories.filter((entry) => entry.id !== action.territoryId) },
        {
          day: save.day,
          minute: save.minute,
          text: `${action.territoryTitle} отжали обратно: авторитета не хватило удержать.`,
        },
      );

    case 'questStep': {
      const quests: Record<string, QuestState> = {
        ...save.quests,
        [action.questId]: { state: 'active', step: action.step },
      };
      return withLog(
        { ...save, quests },
        { day: save.day, minute: save.minute, text: `${action.title}: ${action.text}` },
      );
    }

    case 'questDone': {
      const quests: Record<string, QuestState> = {
        ...save.quests,
        [action.questId]: { state: 'done', step: 0 },
      };
      let next: SaveDoc = {
        ...save,
        quests,
        owned: action.reward.item ? [...save.owned, action.reward.item] : save.owned,
        player: {
          ...save.player,
          avtoritet: save.player.avtoritet + (action.reward.avtoritet ?? 0),
        },
      };
      if (action.reward.xp) next = grantXp(next, action.reward.xp);
      next = withLog(next, {
        day: save.day,
        minute: save.minute,
        text: `${action.title} — закрыт. ${action.reward.text}`,
      });
      return action.reward.cash ? applyMoney(next, action.reward.cash, `${action.title}: награда`) : next;
    }

    case 'settleCourt': {
      // Вопрос решён: следствие закрывается целиком, а не откладывается.
      const settled: SaveDoc = { ...save, justice: { raids: 0, lastRaidDay: 0, courtDay: null } };
      return applyMoney(settled, -action.price, `${action.how}: дело закрыли`);
    }

    case 'finishRun':
      return withLog(
        { ...save, outcome: action.outcome },
        { day: save.day, minute: save.minute, text: action.text },
      );

    case 'hospital': {
      const patched: SaveDoc = {
        ...save,
        minute: DAY_LENGTH_MINUTES,
        habar: [],
        player: { ...save.player, health: 40, stress: clampGauge(save.player.stress + 10) },
      };
      return applyMoney(
        withLog(patched, {
          day: save.day,
          minute: DAY_LENGTH_MINUTES,
          text: 'Больничка: неделя гипса за один вечер. День кончен.',
        }),
        -action.lostCash,
        'Больничка',
      );
    }

    case 'sleep':
      return sleep(save, action.territoryBonus ?? 0);

    case 'log':
      return withLog(save, { day: save.day, minute: save.minute, text: action.text });

    default: {
      const never: never = action;
      return never;
    }
  }
}

/**
 * Все деньги идут через эту функцию с причиной. Реестра транзакций в одиночной
 * игре нет, но когда он понадобится, добавить его — день работы, а не археология.
 */
export function applyMoney(save: SaveDoc, amountKop: number, reason: string): SaveDoc {
  const cash = Math.max(0, save.player.cash + amountKop);
  return withLog(
    { ...save, player: { ...save.player, cash } },
    { day: save.day, minute: save.minute, text: reason, money: amountKop },
  );
}

function applyJob(save: SaveDoc, action: Extract<Action, { type: 'job' }>): SaveDoc {
  const { outcome } = action;
  const minute = clampMinute(save.minute + action.minutes);
  const touched = action.touch ? [...save.world.touched, action.touch] : save.world.touched;

  const next: SaveDoc = {
    ...save,
    minute,
    habar: outcome.success && action.loot ? [...save.habar, action.loot] : save.habar,
    counters: outcome.success ? bumpCounter(save.counters, 'jobs') : save.counters,
    world: { ...save.world, touched },
    player: {
      ...save.player,
      heat: clampGauge(save.player.heat + outcome.heat),
      stress: clampGauge(save.player.stress + outcome.stress),
    },
  };

  if (!outcome.success) {
    return withLog(next, { day: save.day, minute, text: `${action.title}: не вышло. Палево +${outcome.heat}.` });
  }

  // Опыт и уровни: очки статов начисляются здесь же, тратятся отдельным действием.
  const withXp = action.xp ? grantXp(next, action.xp) : next;
  if (outcome.payout > 0) {
    return applyMoney(withXp, outcome.payout, `${action.title}: получилось.`);
  }
  return withLog(withXp, {
    day: save.day,
    minute,
    text: action.loot ? `${action.title}: в руках ${action.loot.title}.` : `${action.title}: получилось.`,
  });
}

/** Ночь: точки приносят доход, палево спадает, стресс уходит, мир тасуется. */
export function sleep(save: SaveDoc, territoryBonus = 0): SaveDoc {
  const player = save.player;
  const gross = save.businesses.reduce(
    (sum, business) => sum + nightIncome(business.incomePerHour, business.level, NIGHT_HOURS),
    0,
  );
  // Территории добавляют долю, бригада забирает свою.
  const brigadeShare = save.brigade.reduce((sum, member) => sum + member.share, 0);
  const income = Math.max(0, Math.round(gross * (1 + territoryBonus / 100) * (1 - Math.min(60, brigadeShare) / 100)));
  const productionHeat = save.businesses.reduce((sum, business) => sum + business.heatPerNight, 0);

  // Без внимания спутница уходит: трое суток и всё.
  const day = save.day + 1;
  const staying = save.companions.filter((companion) => day - companion.lastDateDay <= COMPANION_PATIENCE_DAYS);
  const left = save.companions.filter((companion) => !staying.includes(companion));

  const morning: SaveDoc = {
    ...save,
    day: save.day + 1,
    minute: 0,
    companions: staying,
    // Мир перетасовывается от сида и номера дня, поэтому следы вчерашнего дня стираются.
    world: { ...save.world, touched: [] },
    player: {
      ...player,
      heat: clampGauge(player.heat - nightHeatRelief(stageOf(save.justice, save.day), 16) + productionHeat),
      stress: clampGauge(player.stress - 20),
      health: clampGauge(player.health + 10),
    },
  };

  let woken = withLog(morning, { day: morning.day, minute: 0, text: `Утро ${morning.day}-го дня.` });
  for (const gone of left) {
    woken = withLog(woken, { day: morning.day, minute: 0, text: `${gone.title} больше не берёт трубку.` });
  }
  return income > 0 ? applyMoney(woken, income, `Точки принесли за ночь: ${save.businesses.length} шт.`) : woken;
}

/** Опыт с проверкой уровня: за каждый новый уровень дают очки статов. */
export function grantXp(save: SaveDoc, xp: number): SaveDoc {
  const before = save.player.level;
  const totalXp = save.player.xp + xp;
  const after = levelFromXp(totalXp);
  const player = {
    ...save.player,
    xp: totalXp,
    level: after,
    statPoints: save.player.statPoints + statPointsFor(before, after),
  };
  if (after === before) return { ...save, player };
  return withLog(
    { ...save, player },
    { day: save.day, minute: save.minute, text: `Уровень ${after}. Есть что распределить.` },
  );
}

/** Территории, которые пора защищать: прежний хозяин ждать не будет. */
export function territoriesAtRisk(
  save: SaveDoc,
  rules: Record<string, { retakeAfterDays: number; holdAvtoritet: number; title: string }>,
): { id: string; title: string }[] {
  return save.territories
    .filter((held) => {
      const rule = rules[held.id];
      if (!rule) return false;
      const overdue = save.day - held.heldSince >= rule.retakeAfterDays;
      return overdue && save.player.avtoritet < rule.holdAvtoritet;
    })
    .map((held) => ({ id: held.id, title: rules[held.id]!.title }));
}

export function canAct(save: SaveDoc): boolean {
  return !isDayOver(save.minute) && save.player.stress < 90;
}

function statTitle(stat: StatName | 'sila' | 'smekalochka' | 'obayanie' | 'chotkost'): string {
  const titles: Record<StatName, string> = {
    sila: 'Сила',
    smekalochka: 'Смекалочка',
    obayanie: 'Обаяние',
    chotkost: 'Чоткость',
    avtoritet: 'Авторитет',
  };
  return titles[stat];
}

function clampMinute(minute: number): number {
  return Math.min(DAY_LENGTH_MINUTES, Math.max(0, Math.round(minute)));
}

/** Репутация держится в границах −100…100. */
function bumpNpc(save: SaveDoc, npc: string, delta: number): Record<string, number> {
  const current = save.npc[npc] ?? 0;
  return { ...save.npc, [npc]: Math.max(-100, Math.min(100, current + delta)) };
}

function bumpCounter(counters: Record<string, number>, key: string): Record<string, number> {
  return { ...counters, [key]: (counters[key] ?? 0) + 1 };
}

function clampGauge(value: number): number {
  return Math.min(100, Math.max(0, Math.round(value)));
}

function withLog(save: SaveDoc, entry: LogEntry): SaveDoc {
  return { ...save, log: [...save.log.slice(-99), entry] };
}
